import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import { ServiceQueuePanel } from "@/components/queue/ServiceQueuePanel";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { billingSnapshotForLabOrder } from "@/core/billing/service-billing-status";
import { listConsultationsByPatient } from "@/core/clinical/clinical-store";
import { consultationDetailPath } from "@/core/clinical/clinical-store";
import {
  createLabOrder,
  LabOrderValidationError,
  listLabOrders,
  syncLabOrdersFromApi,
  updateLabOrderStatus,
} from "@/core/laboratory/lab-store";
import {
  COMMON_LAB_TESTS,
  LAB_STATUS_LABELS,
  labOrderConsultationLabel,
  type LabOrder,
  type LabOrderStatus,
} from "@/core/laboratory/types";
import {
  loadAppointmentPractitioners,
  listAppointmentPractitionersSync,
  practitionerDisplayName,
} from "@/core/practitioners/practitioner-store";
import type { AppointmentPractitioner } from "@/core/practitioners/types";
import { getPatientById, listPatients } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

const STATUS_FILTER: Array<LabOrderStatus | ""> = [
  "",
  "requested",
  "pending_insurer",
  "in_progress",
  "completed",
  "cancelled",
];

export function LaboratoryPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<LabOrderStatus | "">("");
  const [patientId, setPatientId] = useState("");
  const [prescriberId, setPrescriberId] = useState("");
  const [consultationId, setConsultationId] = useState("");
  const [testName, setTestName] = useState(COMMON_LAB_TESTS[0]);
  const [customTest, setCustomTest] = useState("");
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formAlert, setFormAlert] = useState<string | null>(null);
  const [practitioners, setPractitioners] = useState<AppointmentPractitioner[]>(() =>
    listAppointmentPractitionersSync(),
  );

  useEffect(() => {
    let cancelled = false;
    setSyncing(true);
    void syncLabOrdersFromApi().finally(() => {
      if (!cancelled) {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let active = true;
    loadAppointmentPractitioners().then((list) => {
      if (active) setPractitioners(list);
    });
    return () => {
      active = false;
    };
  }, []);

  const patientConsultations = useMemo(() => {
    if (!patientId) return [];
    return listConsultationsByPatient(patientId);
  }, [patientId, refreshKey]);

  useEffect(() => {
    setConsultationId("");
  }, [patientId]);

  const orders = useMemo(() => {
    void refreshKey;
    const all = listLabOrders();
    return statusFilter ? all.filter((o) => o.status === statusFilter) : all;
  }, [refreshKey, statusFilter]);

  const patients = useMemo(() => {
    void refreshKey;
    return listPatients();
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    const test = testName === "Autre" ? customTest.trim() : testName;
    if (!patientId) {
      setFormError("Sélectionnez un patient.");
      return;
    }
    if (!prescriberId) {
      setFormError("Prescripteur requis.");
      return;
    }
    if (!test) {
      setFormError("Indiquez le type d'analyse.");
      return;
    }
    try {
      const result = createLabOrder({
        patientId,
        prescriberId,
        consultationId: consultationId || null,
        testName: test,
        clinicalNotes,
      });
      setClinicalNotes("");
      setFormError(null);
      setFormAlert(
        result.insurerPending
          ? (result.alertMessage ??
              "Demande créée — validation assureur en cours. Le prélèvement peut démarrer.")
          : null,
      );
      refresh();
    } catch (err) {
      setFormError(
        err instanceof LabOrderValidationError ? err.message : "Impossible de créer la demande.",
      );
    }
  };

  return (
    <div>
      <PageHeader
        title="Laboratoire — analyses"
        description="Demandes d'analyses, prescripteur, lien consultation et suivi des statuts."
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={routes.supplyConsumablesArticles} className="btn btn-outline-secondary btn-sm">
              Stock consommables (WH-CONS)
            </Link>
            <Link to={routes.labResults} className="btn btn-outline-secondary btn-sm">
              Résultats & impression
            </Link>
          </div>
        }
      />

      <ServiceQueuePanel
        serviceId="laboratory"
        defaultUnitName="Laboratoire"
        enableCallNext
        title="Salle d'attente — laboratoire"
      />

      <div className="ph-invoice-sheet ph-lab-register mb-4">
        <h3 className="h6 fw-semibold mb-3">Nouvelle demande</h3>
        <form className="row g-3 align-items-end ph-lab-register-form" onSubmit={handleCreate}>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold mb-1" htmlFor="lab-patient">
              Patient
            </label>
            <select
              id="lab-patient"
              className="form-select form-select-sm"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              required
            >
              <option value="">— Choisir —</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>
                  {patientFullName(p)} ({p.fileNumber})
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold mb-1" htmlFor="lab-prescriber">
              Prescripteur (médecin)
            </label>
            <select
              id="lab-prescriber"
              className="form-select form-select-sm"
              value={prescriberId}
              onChange={(e) => setPrescriberId(e.target.value)}
              required
            >
              <option value="">— Choisir un médecin —</option>
              {practitioners.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.displayName} — {doc.department}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold mb-1" htmlFor="lab-consultation">
              Consultation liée (optionnel)
            </label>
            <select
              id="lab-consultation"
              className="form-select form-select-sm"
              value={consultationId}
              onChange={(e) => setConsultationId(e.target.value)}
              disabled={!patientId}
            >
              <option value="">— Aucune / hors consultation —</option>
              {patientConsultations.map((con) => (
                <option key={con.id} value={con.id}>
                  {new Date(con.createdAt).toLocaleDateString("fr-FR")} — {con.motif || "Sans motif"}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label small fw-semibold mb-1" htmlFor="lab-test">
              Analyse
            </label>
            <select
              id="lab-test"
              className="form-select form-select-sm"
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
            >
              {COMMON_LAB_TESTS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
              <option value="Autre">Autre…</option>
            </select>
          </div>
          {testName === "Autre" && (
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small fw-semibold mb-1" htmlFor="lab-custom">
                Précision
              </label>
              <input
                id="lab-custom"
                className="form-control form-control-sm"
                value={customTest}
                onChange={(e) => setCustomTest(e.target.value)}
              />
            </div>
          )}
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold mb-1" htmlFor="lab-notes">
              Indication clinique
            </label>
            <input
              id="lab-notes"
              className="form-control form-control-sm"
              placeholder="Motif de la demande"
              value={clinicalNotes}
              onChange={(e) => setClinicalNotes(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-2">
            <button type="submit" className="btn btn-primary btn-sm w-100">
              Demander
            </button>
          </div>
        </form>
        {formError && <p className="small text-danger mb-0 mt-2">{formError}</p>}
        {formAlert && (
          <div className="alert alert-warning py-2 small mt-2 mb-0">{formAlert}</div>
        )}
      </div>

      <div className="ph-invoice-sheet ph-lab-filters mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold mb-1" htmlFor="lab-status">
              Filtrer par statut
            </label>
            <select
              id="lab-status"
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as LabOrderStatus | "")}
            >
              <option value="">Tous</option>
              {STATUS_FILTER.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {LAB_STATUS_LABELS[s as LabOrderStatus]}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-8 text-md-end small text-muted">
            {orders.length} demande(s)
          </div>
        </div>
      </div>

      <div className="ph-invoice-sheet">
        {orders.length === 0 ? (
          <p className="text-muted mb-0">Aucune demande d&apos;analyse.</p>
        ) : (
          <LabOrderTable
            orders={orders}
            practitioners={practitioners}
            statusFilter={statusFilter}
            onRefresh={refresh}
          />
        )}
      </div>
    </div>
  );
}

function LabOrderTable({
  orders,
  practitioners,
  statusFilter,
  onRefresh,
}: {
  orders: LabOrder[];
  practitioners: AppointmentPractitioner[];
  statusFilter: LabOrderStatus | "";
  onRefresh: () => void;
}) {
  const pagination = useClientPagination(orders, [statusFilter], 25);
  const { pagedItems } = pagination;

  return (
    <div className="table-responsive ph-diag-table">
      <table className="table table-sm ph-dash-table align-middle mb-0">
        <thead>
          <tr>
            <th>N°</th>
            <th>Patient</th>
            <th>Analyse</th>
            <th>Prescripteur</th>
            <th>Consultation</th>
            <th>Statut</th>
            <th>Facturation</th>
            <th>Date</th>
            <th className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pagedItems.map((order) => {
            const patient = getPatientById(order.patientId);
            const prescriberName = practitionerDisplayName(
              order.prescriberId,
              order.prescriber,
              practitioners,
            );
            return (
              <tr key={order.id} className="ph-diag-row">
                <td data-label="N°">
                  <span className="ph-inv-badge">{order.orderNumber}</span>
                </td>
                <td data-label="Patient" className="fw-medium">
                  {patient ? patientFullName(patient) : "—"}
                </td>
                <td data-label="Analyse" className="small">
                  {order.testName}
                </td>
                <td data-label="Prescripteur" className="small text-muted">
                  {prescriberName}
                </td>
                <td data-label="Consultation">
                  {order.consultationId ? (
                    <Link to={consultationDetailPath(order.consultationId)}>
                      {labOrderConsultationLabel(order.consultationId)}
                    </Link>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td data-label="Statut">
                  <span className={`hosp-diag-badge hosp-diag-badge--${order.status}`}>
                    {LAB_STATUS_LABELS[order.status]}
                  </span>
                </td>
                <td data-label="Facturation">
                  <ServiceBillingBadge
                    snapshot={billingSnapshotForLabOrder(order.id)}
                    compact
                  />
                </td>
                <td data-label="Date" className="small text-muted">
                  {new Date(order.createdAt).toLocaleString("fr-FR")}
                </td>
                <td data-label="Actions" className="text-end">
                  {order.status === "requested" && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 me-2"
                      onClick={() => {
                        updateLabOrderStatus(order.id, "in_progress");
                        onRefresh();
                      }}
                    >
                      Prendre en charge
                    </button>
                  )}
                  {order.status === "in_progress" && (
                    <Link to={routes.labResults} className="btn btn-link btn-sm p-0">
                      Saisir résultat
                    </Link>
                  )}
                  {order.status === "completed" && (
                    <Link to={routes.labResults} className="btn btn-link btn-sm p-0">
                      Voir résultat
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <TablePagination
        page={pagination.page}
        pageSize={pagination.pageSize}
        totalItems={pagination.totalItems}
        onPageChange={pagination.setPage}
        onPageSizeChange={pagination.setPageSize}
      />
    </div>
  );
}
